import React, { useState } from 'react';
import {
  IonButton,
  IonRouterLink,
  IonRow,
  IonCol,
  IonGrid,
  IonItem,
  IonCheckbox,
  IonLabel,
  IonLoading,
  IonModal,
  IonContent,
  IonTitle,
  IonText,
  IonCardTitle,
  IonButtons,
  IonIcon
} from '@ionic/react';
import './style.scss';
import { validate as nirValidate } from 'validcq';
import { register } from '../../store/actions/authentication';
import { RegistrationParams } from '../../repositories/AuthenticationRepository';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { STRINGS } from '../../i18n/strings';
import FormError from '../FormError';
import EventBus from '../../services/EventBus';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import { User } from '../../models';
import { nirFormat } from '../../const';
import PasswordStrengthBar from 'react-password-strength-bar';
import zxcvbn from 'zxcvbn';
import Field from '../Field';
import { close } from 'ionicons/icons';

import {
  noSpecialCharacters,
  noSpecialCharactersNorNumbersFilter
} from '../../utils/validators';
import isIOS from '../../utils/isIOS';
import moment from 'moment';

type Inputs = {
  email: string;
  password: string;
  passwordConfirm: string;
  name: string;
  firstName: string;
  birthName: string;
  sameBirthName: boolean;
  gender: 'm' | 'f' | 'nc' | null;
  birthdate: string | null;
  nir: string;
  noNir: boolean;
  nirAlternative: string;
  mutual: File | null;
  line1: string;
  postcode: string;
  city: string;
  country: { value: string; label: string };
  phone: string;
  referringDoctor: string;
  alreadyClient: boolean;
  consent_use_of_medical_data: boolean;
};

type RegistrationFormProps = {
  onSubmit?: Function;
  onRegisterSuccess?: Function;
  onRegisterError?: Function;
  loginRouterLink?: string;
};

const mapStateToProps = (
  state: {
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    sidep: state.appointments.sidep,
    currentAppointmentLaboratory: state.appointments.appointment.laboratory,
    hasSelectedOtherPatient: state.appointments.appointment.patient
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  dispatchRegister: (params: RegistrationParams) => dispatch(register(params))
});

const RegistrationForm: React.FC<
  ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    RegistrationFormProps
> = ({
  dispatchRegister,
  onSubmit = () => {},
  onRegisterSuccess = (user: User) => {},
  onRegisterError = () => {},
  loginRouterLink = '/dashboard/login',
  sidep,
  hasSelectedOtherPatient,
  currentAppointmentLaboratory = {}
}) => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { registration_options } = currentAppointmentLaboratory;
  const hasAbroadNir =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.no_nir;
  const hasCertificate =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.certificate_reimbursement;
  const isMutualRequired =
    currentAppointmentLaboratory &&
    registration_options &&
    registration_options.mutual_required;
  const { control, handleSubmit, setValue, getValues, errors, watch } =
    useForm<Inputs>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        email: '',
        password: '',
        passwordConfirm: '',
        name: '',
        firstName: '',
        birthName: '',
        sameBirthName: false,
        gender: null,
        birthdate: null,
        nir: '',
        noNir: false,
        nirAlternative: '',
        line1: '',
        mutual: null,
        postcode: '',
        city: '',
        country: { value: 'fr', label: 'France' },
        phone: '',
        referringDoctor: '',
        alreadyClient: false,
        consent_use_of_medical_data: false
      }
    });
  const [nirAlternative, setNirAlternative] = useState<File>();
  const [ticketModerateur, setTicketModerateur] = useState<File>();

  function onClickRegister(data: Inputs) {
    onSubmit();
    setLoading(true);
    let splittedBirthdate: any = [];
    let birthdateFormatted: any = moment().format('YYYY-MM-DD');
    if (data.birthdate) {
      splittedBirthdate = data.birthdate.split('/');
      birthdateFormatted = `${splittedBirthdate[2]}-${splittedBirthdate[1]}-${splittedBirthdate[0]}`;
    }
    const payload = {
      ...data,
      gender: data.gender || 'nc',
      birthdate: birthdateFormatted,
      nir: data.nir.replace(/[^0-9a-zA-Z)]/g, ''),
      address_line_1: data.line1,
      // @ts-ignore. NOTE: This is temporary.
      referring_doctor: data.searchReferringDoctor,
      country: data.country.value.toLowerCase(),
      sidep: !hasSelectedOtherPatient ? sidep : undefined,
      nirAlternative,
      ticketModerateur,
      isRopc: true
    };
    dispatchRegister(payload)
      .then(({ user }: { user: User }) => {
        onRegisterSuccess(user);
        EventBus.success(`Bienvenue chez Unilabs, ${data.firstName}.`);
      })
      .catch((err: any) => {
        setLoading(false);
        onRegisterError();
      });
  }

  function showError(fieldName: string) {
    return (
      (errors as any)[fieldName] && (
        <ErrorMessage
          errors={errors}
          name={fieldName}
          render={({ message }) => <FormError message={message as string} />}
        />
      )
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onClickRegister)}
      autoComplete="off"
      className="registration-form"
      onKeyUp={(e) => {
        if (e.which === 13) {
          handleSubmit(onClickRegister)(e);
        }
      }}
    >
      <IonLoading isOpen={loading} />
      <IonGrid>
        <IonRow className="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations de compte
            </legend>
            <IonRow>
              <Field
                type="email"
                controllerProps={{
                  control,
                  name: 'email',
                  rules: {
                    pattern: {
                      value:
                        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                      message: STRINGS.FR.FORM_ERROR_FIELD_EMAIL_INCORRECT
                    },
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'lepass@unilabs.fr'
                }}
                label="Email"
              />
            </IonRow>
            <IonRow>
              <Field
                type="password"
                controllerProps={{
                  control,
                  name: 'password',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    min: 6,
                    validate: (value) =>
                      zxcvbn(value).score >= 3 ||
                      STRINGS.FR.FORM_ERROR_FIELD_PASSWORD_NOT_STRONG_ENOUGH
                  }
                }}
                errors={errors}
                isMarginless
                label="Mot de passe"
                note={`Le mot de passe doit avoir un niveau de sécurité de niveau bon ou fort.`}
              >
                <PasswordStrengthBar
                  password={watch('password')}
                  scoreWords={['faible', 'faible', 'moyen', 'bon', 'fort']}
                  minLength={0}
                />
                <IonLabel className="registration-form__label">
                  <small>
                    Le mot de passe idéal ? Plus de 12 caractères, aléatoirement
                    minuscules, majuscules, caractères spéciaux et chiffres.
                  </small>
                </IonLabel>
              </Field>
            </IonRow>
            <IonRow>
              <Field
                type="password"
                controllerProps={{
                  control,
                  name: 'passwordConfirm',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: (value) =>
                      value === watch('password') ||
                      STRINGS.FR.FORM_ERROR_FIELD_PASSWORD_NOT_EQUAL
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Confirmez votre mot de passe*'
                }}
              />
            </IonRow>
          </fieldset>
        </IonRow>
        <IonRow className="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations personnelles
            </legend>
            <IonRow>
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'firstName',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharactersNorNumbersFilter
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Nathalie',
                  autocapitalize: 'on'
                }}
                isMarginless
                label="Prénom"
              />
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'name',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharactersNorNumbersFilter
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Dupont',
                  autocapitalize: 'on'
                }}
                onIonInput={(e, onChange) => {
                  const value = (e.target as HTMLInputElement).value;
                  onChange(value);
                  if (getValues('sameBirthName')) {
                    setValue('birthName', value);
                  }
                }}
                isMarginless
                label="Nom (d'usage)"
              />
            </IonRow>
            <IonRow>
              <IonCol>
                <IonItem color="transparent" lines="none">
                  <IonLabel>Mon nom de naissance est identique</IonLabel>
                  <Controller
                    control={control}
                    name="sameBirthName"
                    render={({ onChange, ref, value, name }) => (
                      <IonCheckbox
                        name={name}
                        checked={value}
                        onIonChange={(e) => {
                          setValue('birthName', getValues('name'));
                          onChange(e.detail.checked);
                        }}
                        ref={ref}
                      />
                    )}
                  />
                </IonItem>
                <Field
                  type="text"
                  controllerProps={{
                    control,
                    name: 'birthName',
                    rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                  }}
                  errors={errors}
                  inputProps={{
                    placeholder: 'Martin',
                    autocomplete: 'new-password',
                    autocapitalize: 'on'
                  }}
                  onIonInput={(e, onChange) => {
                    const value = (e.target as HTMLInputElement).value;
                    onChange(value);
                    if (getValues('sameBirthName')) {
                      setValue('name', value);
                    }
                  }}
                  label={<>Nom de naissance</>}
                  note={`Le nom de naissance doit être identiqué si différent de votre nom d'usage`}
                />
              </IonCol>
            </IonRow>
            <IonRow>
              <Field
                type="options"
                controllerProps={{
                  control,
                  name: 'gender',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                optionsProps={{
                  items: [
                    { value: 'm', name: 'Homme' },
                    { value: 'f', name: 'Femme' }
                  ]
                }}
                errors={errors}
                label="Sexe"
              />
            </IonRow>
            <IonRow>
              <Field
                type="datetime-local"
                controllerProps={{
                  control,
                  name: 'birthdate',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Date de naissance*'
                }}
                label="Date de naissance"
              />
            </IonRow>
            <IonRow>
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'line1',
                  rules: {
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED,
                    validate: noSpecialCharacters
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: '1 rue Mozart',
                  autocapitalize: 'on'
                }}
                label="Rue et numéro de rue"
              />
            </IonRow>
            <IonRow>
              <Field
                type={isIOS() ? 'tel' : 'number'}
                controllerProps={{
                  control,
                  name: 'postcode',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: '92110'
                }}
                label="Code postal"
              />
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'city',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Clichy',
                  autocapitalize: 'on'
                }}
                label="Ville"
              />
            </IonRow>
            <IonRow>
              <Field
                type="select"
                controllerProps={{
                  control,
                  name: 'country',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                selectProps={{
                  placeholder: 'Pays*',
                  type: 'country'
                }}
                label="Pays"
              />
            </IonRow>
            <IonRow>
              <Field
                type="tel"
                hasSpecialPhoneInput
                controllerProps={{
                  control,
                  name: 'phone',
                  rules: { required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Numéro de téléphone'
                }}
                label="Téléphone"
              />
            </IonRow>
          </fieldset>
        </IonRow>
        <IonRow class="registration-form__fieldset">
          <fieldset>
            <legend className="registration-form__title">
              Vos informations de santé
            </legend>
            <IonRow>
              <Field
                type="text"
                isFormatted
                controllerProps={{
                  control,
                  name: 'nir',
                  rules: {
                    validate: (value) =>
                      nirValidate(`${value}`) ||
                      !!getValues('noNir') ||
                      STRINGS.FR.FORM_ERROR_FIELD_NIR_INCORRECT,
                    required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                  }
                }}
                errors={errors}
                inputProps={{
                  format: nirFormat,
                  placeholder: '1 96 11 75 005 005 32'
                }}
                label="N° Sécurité sociale"
              >
                {hasAbroadNir && (
                  <IonItem color="transparent" lines="none">
                    <IonLabel>
                      Je ne suis pas affilié à la sécurité sociale française
                    </IonLabel>
                    <Controller
                      control={control}
                      name="noNir"
                      render={({ onChange, ref, value, name }) => (
                        <IonCheckbox
                          name={name}
                          checked={value}
                          onIonChange={(e) => {
                            onChange(e.detail.checked);
                          }}
                          ref={ref}
                        />
                      )}
                    />
                  </IonItem>
                )}
                {hasAbroadNir && watch('noNir') ? (
                  <>
                    <IonLabel className="registration-form__label">
                      <small>
                        Si vous dépendez de la CFE, du 100% GMC HENNER ou
                        disposez d'une prise en charge ambassade, merci
                        d'ajouter votre justificatif
                      </small>
                    </IonLabel>
                    <input
                      name="nirAlternative"
                      type="file"
                      className="custom-input"
                      accept=".pdf, image/png, image/jpeg"
                      onChange={(e) => {
                        setNirAlternative(
                          e.target.files ? e.target.files[0] : undefined
                        );
                      }}
                    />
                  </>
                ) : null}
              </Field>
            </IonRow>
            <IonRow>
              <Field
                type="file"
                controllerProps={{
                  control,
                  name: 'mutual',
                  rules: isMutualRequired
                    ? {
                        required: STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
                      }
                    : {}
                }}
                inputProps={{
                  accept: '.pdf, image/png, image/jpeg',
                  className: 'custom-input',
                  name: 'mutual'
                }}
                errors={errors}
                hasOptionalWording
                label="Mutuelle"
              />
            </IonRow>
            {hasCertificate && (
              <IonRow>
                <Field
                  type="file"
                  inputProps={{
                    accept: '.pdf, image/png, image/jpeg',
                    className: 'custom-input',
                    name: 'ticketModerateur',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      setTicketModerateur(
                        e.target.files ? e.target.files[0] : undefined
                      );
                    }
                  }}
                  label={
                    <>
                      Attestation de prise en charge à 100%{' '}
                      <small>(facultatif)</small>
                    </>
                  }
                />
              </IonRow>
            )}
            <IonRow>
              <Field
                type="text"
                controllerProps={{
                  control,
                  name: 'searchReferringDoctor',
                  rules: {
                    validate: noSpecialCharactersNorNumbersFilter
                  }
                }}
                errors={errors}
                inputProps={{
                  placeholder: 'Nom du médecin traitant (facultatif)',
                  autocomplete: 'new-password'
                }}
              />
            </IonRow>
          </fieldset>
        </IonRow>

        <IonItem lines="none" className="no-background">
          <Controller
            control={control}
            name="consent_use_of_medical_data"
            rules={{
              validate: (value) =>
                value === true || STRINGS.FR.FORM_ERROR_FIELD_REQUIRED
            }}
            render={({ onChange, onBlur, ref, value, name }) => (
              <IonCheckbox
                name={name}
                checked={value}
                onIonChange={(e) => onChange(e.detail.checked)}
                onIonBlur={onBlur}
                className="ion-margin-end"
                ref={ref}
              />
            )}
          />
          <IonLabel className="ion-text-wrap">
            J'accepte les{' '}
            <a href="/dashboard/cgu" rel="noopener noreferrer" target="_blank">
              conditions générales d'utilisation
            </a>{' '}
            et la{' '}
            <span
              onClick={() => setIsOpen(true)}
              className="notice-information-link"
            >
              notice d'information relatives aux données personnelles
            </span>{' '}
            des services de My-Unilabs.fr
          </IonLabel>

          <IonModal isOpen={isOpen}>
            <IonContent className="ion-padding">
              <IonButtons
                onClick={() => setIsOpen(false)}
                icon-only
                className="ion-padding ion-button"
              >
                <IonIcon icon={close} />
              </IonButtons>
              <IonTitle className="ion-padding ion-text-center ion-margin-top">
                {STRINGS.FR.INFORMATION_NOTICE_TITLE}
              </IonTitle>
              <div className="ion-padding">
                <IonCardTitle className="ion-padding-bottom">
                  {STRINGS.FR.INFORMATION_NOTICE_QUESTION_1}
                </IonCardTitle>
                <IonText>
                  {STRINGS.FR.INFORMATION_NOTICE_TEXT_QUESTION_1}
                  <br />
                </IonText>
                <ul className="list">
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_ITEM_1}
                    </IonLabel>
                  </li>
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_ITEM_2}
                    </IonLabel>
                  </li>
                </ul>
                <IonText>
                  {STRINGS.FR.INFORMATION_NOTICE_TEXT_2_QUESTION_1}
                </IonText>
                <IonCardTitle className="ion-padding-bottom ion-padding-top">
                  {STRINGS.FR.INFORMATION_NOTICE_QUESTION_2}
                </IonCardTitle>
                <IonText>
                  {STRINGS.FR.INFORMATION_NOTICE_TEXT_QUESTION_2}
                  <br />
                </IonText>
                <ul className="list">
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_QUESTION_2_ITEM_1}
                    </IonLabel>
                  </li>
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_QUESTION_2_ITEM_2}
                    </IonLabel>
                  </li>
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_QUESTION_2_ITEM_3}
                    </IonLabel>
                  </li>
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_QUESTION_2_ITEM_4}
                    </IonLabel>
                  </li>
                  <li>
                    <IonLabel>
                      {STRINGS.FR.INFORMATION_NOTICE_LIST_QUESTION_2_ITEM_5}
                    </IonLabel>
                  </li>
                </ul>
                <IonText>
                  {STRINGS.FR.INFORMATION_NOTICE_MAIL}
                  <a
                    href="mailto:dpo@unilabs.com"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {' '}
                    DPO@unilabs.com
                  </a>
                  . {STRINGS.FR.INFORMATION_NOTICE_CNIL}
                </IonText>
              </div>
            </IonContent>
          </IonModal>
        </IonItem>
        {showError('consent_use_of_medical_data')}
      </IonGrid>
      <div className="ion-text-center ion-padding-horizontal ion-margin-top">
        <IonButton type="submit" expand="block">
          Créer mon compte
        </IonButton>
        <div className="ion-margin-top">
          <IonRouterLink routerLink={loginRouterLink} routerDirection="none">
            J'ai déjà un compte
          </IonRouterLink>
        </div>
      </div>
    </form>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RegistrationForm);
